import Decimal from "decimal.js";

// Same bounds the backend enforces: 18 integer and 8 fractional digits.
export const AMOUNT_RE = /^-?\d{1,18}(\.\d{1,8})?$/;
export const RATE_RE = /^\d{1,18}(\.\d{1,8})?$/;

// Backend rounds half away from zero to 8 places; ROUND_HALF_UP matches that.
const inBase = (amount, rate) =>
  new Decimal(amount).times(rate).toDecimalPlaces(8, Decimal.ROUND_HALF_UP);

// Currencies are matched by name, case-insensitively, as the backend does.
export const isBase = (currency, base) =>
  String(currency ?? "").trim().toUpperCase() === String(base ?? "").trim().toUpperCase();

// Positive amount = debit, negative = credit, both in the base currency.
export function lineDrCr(amount, rate) {
  if (!AMOUNT_RE.test(amount ?? "") || !RATE_RE.test(rate ?? "")) return { debit: "0", credit: "0" };
  const v = inBase(amount, rate);
  return v.isNeg() ? { debit: "0", credit: v.abs().toFixed() } : { debit: v.toFixed(), credit: "0" };
}

export function totals(lines) {
  let debit = new Decimal(0);
  let credit = new Decimal(0);
  for (const l of lines) {
    const x = lineDrCr(l.amount, l.rate);
    debit = debit.plus(x.debit);
    credit = credit.plus(x.credit);
  }
  return { debit: debit.toFixed(), credit: credit.toFixed(), difference: debit.minus(credit).toFixed() };
}

// `base` is the base currency name: a line in it must carry a rate of 1.
export function lineOk(l, base = "USDT") {
  if (!l.account || !l.currency || !AMOUNT_RE.test(l.amount) || !RATE_RE.test(l.rate)) return false;
  if (new Decimal(l.rate).lte(0)) return false;
  if (isBase(l.currency, base) && !new Decimal(l.rate).eq(1)) return false;
  return !inBase(l.amount, l.rate).isZero();
}

export function canSave(lines, base = "USDT") {
  if (lines.length < 2 || lines.length > 200 || !lines.every((l) => lineOk(l, base))) return false;
  const t = totals(lines);
  return t.debit === t.credit;
}

// Edit replaces the whole receipt: anything left out goes blank, today or
// "General" on the backend, so every field is always sent. The original
// timestamp is kept when the day did not change. Lines send the currency by
// name; the backend stores the matching code and name beside it.
export function receiptBody(form, receipt) {
  const body = {
    fiscalYear: form.fiscalYear,
    date: receipt && receipt.date.slice(0, 10) === form.date ? receipt.date : form.date,
    receiptType: form.receiptType,
    description: form.description,
    costCenter: form.costCenter || null,
    copyDescription: form.copyDescription,
    lines: form.lines.map(({ account, currency, amount, rate, description }) => ({
      account,
      currency,
      amount,
      rate,
      description,
    })),
  };
  if (receipt) body.version = receipt.version;
  else body.requestId = form.requestId;
  return body;
}
